import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Dialog } from "component/shared/dialog";
import { DialogTitle } from "component/shared/dialog/title";
import { useAppThunkDispatch } from "redux/store";
import { updateCampaignDisplayCategory } from "redux/slice/campaign-display-categories";
import { CampaignCategoryGroup } from "shared/campaign-grouping";
import { styles } from "./style";

interface OwnProps {
	// The category being renamed. Rendered only for real categories (never Uncategorized).
	group?: CampaignCategoryGroup;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * MAX-3878 — Rename Category modal (figma 1009:10751). Copy verbatim: title "Rename Category",
 * field "Name*" prefilled with the current name, buttons "Cancel" / "Save Changes".
 */
const Component: React.FC<Props> = ({ group, onClose, classes = {} }) => {
	const [name, setName] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const dispatch = useAppThunkDispatch();
	const open = !!group;

	// Prefill with the current name each time the modal opens for a category.
	useEffect(() => {
		if (group) {
			setName(group.name);
			setSubmitted(false);
		}
	}, [group]);

	const nameError = submitted && !name.trim();

	const handleSave = () => {
		setSubmitted(true);
		if (!group || !name.trim()) {
			return;
		}
		dispatch(updateCampaignDisplayCategory({ id: group.id, name: name.trim() }));
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle title="Rename Category" onClose={onClose} />
			<Mui.DialogContent>
				<Mui.TextField
					className={classes.field}
					variant="outlined"
					margin="dense"
					label="Name*"
					value={name}
					error={nameError}
					helperText={nameError ? "Name is required." : undefined}
					onChange={(e) => setName(e.target.value)}
					fullWidth
					autoFocus
				/>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions}>
				<Mui.Button variant="outlined" color="primary" onClick={onClose}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" color="primary" onClick={handleSave}>
					Save Changes
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const RenameCategoryDialog = withStyles(Component, styles);
